/**
 * Protected Route Component - רכיב נתיב מוגן
 * Protects routes that require authentication and/or questionnaire completion
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { ReactNode, useEffect, useState } from 'react'
import api from '../../services/api'
import { isStaffRole } from '../../config/tracks'
import type { UserRole } from '../../types'

interface ProtectedRouteProps {
  children: ReactNode
  requireQuestionnaire?: boolean
  requireRole?: UserRole[]
  /**
   * Whether the advisory onboarding chain (agreement → risk questionnaire → risk
   * approval) applies to this route.
   *
   * DEFAULTS TO TRUE so every route that existed before portal tracks behaves
   * exactly as it did. Only the free track routes opt out — they are unsigned,
   * unpaid explanatory areas, and pushing a /pensia visitor into an advisory
   * agreement would be wrong. A client who later wants the advisory product still
   * meets the full chain on /dashboard and /chat.
   */
  requireClientOnboarding?: boolean
}

export default function ProtectedRoute({
  children,
  requireQuestionnaire = false,
  requireRole,
  requireClientOnboarding = true,
}: ProtectedRouteProps) {
  const { isAuthenticated, user, updateUser } = useAuthStore()
  const location = useLocation()
  const [isChecking, setIsChecking] = useState(false)

  // Fetch current user data if questionnaire info is missing
  useEffect(() => {
    if (requireQuestionnaire && user && !user.questionnaireExpiresAt && !isChecking) {
      setIsChecking(true)
      api.get('/auth/me')
        .then((response) => {
          if (response.data?.user) {
            updateUser(response.data.user)
          }
        })
        .catch((error) => {
          console.error('Failed to fetch user data:', error)
        })
        .finally(() => {
          setIsChecking(false)
        })
    }
  }, [requireQuestionnaire, user, updateUser, isChecking])

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Staff (admin/advisor) are not clients — they skip the client-onboarding gates
  // (advisory agreement → risk questionnaire → risk approval) and go straight to the app.
  // Students (school learning tool) are not advisory clients either — they skip the
  // same onboarding entirely.
  const isStaff = isStaffRole(user.role)
  const isStudent = user.role === 'student'
  const skipClientOnboarding = isStaff || isStudent || !requireClientOnboarding

  // Students (school learning tool) stay inside their own pages — never the
  // advisory dashboard/chat/subscription, and not the adult tracks either: they
  // may be minors, and תכנון פרישה / משכנתא are not written for them. Anything
  // else sends them back to /learn.
  if (isStudent) {
    const studentAllowed = ['/learn', '/profile']
    if (!studentAllowed.some((p) => location.pathname.startsWith(p))) {
      return <Navigate to="/learn" replace />
    }
  }

  if (!skipClientOnboarding) {
    // Agreement must be signed before accessing any protected route
    if (!user.agreementSigned) {
      return <Navigate to="/agreement" state={{ from: location }} replace />
    }

    // Questionnaire must be completed before accessing any route except /questionnaire itself
    if (!user.lastQuestionnaireCompletedAt && location.pathname !== '/questionnaire') {
      return <Navigate to="/questionnaire" state={{ from: location }} replace />
    }

    // Risk approval must be signed after completing questionnaire
    if (user.lastQuestionnaireCompletedAt && user.riskApprovalSigned !== true && location.pathname !== '/questionnaire') {
      return <Navigate to="/risk-approval" state={{ from: location }} replace />
    }
  }

  // NOTE: free users are intentionally NOT redirected to the subscription page.
  // They use the app freely; the only paywall is on the chat (sending a message
  // returns the "arrange payment" reply + link). The subscription page stays
  // reachable from the nav for anyone who wants to subscribe.

  // Check role-based access
  if (requireRole && !requireRole.includes(user.role as any)) {
    return (
      <Navigate
        to="/dashboard"
        state={{ message: 'אין לך הרשאות לגשת לדף זה' }}
        replace
      />
    )
  }

  // Show loading while checking
  if (requireQuestionnaire && !user.questionnaireExpiresAt && isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner w-12 h-12" />
      </div>
    )
  }

  // Check if questionnaire is required and user has completed it (staff exempt)
  if (requireQuestionnaire && !isStaff) {
    // Check if questionnaire exists and is not expired
    const hasValidQuestionnaire =
      user.questionnaireExpiresAt &&
      new Date(user.questionnaireExpiresAt) > new Date()

    if (!hasValidQuestionnaire) {
      return (
        <Navigate
          to="/questionnaire"
          state={{ from: location, message: 'יש להשלים את שאלון הסיכון לפני שימוש בצ\'אט' }}
          replace
        />
      )
    }
  }

  // Authenticated and authorized - render children
  return <>{children}</>
}
